import React, { useState } from 'react'
import { LoaderBar, PageHeader } from 'components'
import {
    SupportTicketList,
    SupportTicketDetailModal,
    RaiseTicketForm,
} from 'page-components/support-ticket'
import { useSupportTicketsByEmail } from 'hooks/support-ticket'
import { Tab, Form } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
    FormTextFormField,
    FormDropdownField,
    SubmitCancelButtons,
} from 'elements'

const SupportTicketSearchSchema = yup.object().shape({
    SearchText: yup.string(),
    Status: yup.mixed(),
})

const STATUS_OPTIONS = [
    { key: 'all', text: 'All Statuses', value: 'all' },
    { key: '1', text: 'Open', value: 1 },
    { key: '2', text: 'In Progress', value: 2 },
    { key: '3', text: 'Resolved', value: 3 },
    { key: '4', text: 'Closed', value: 4 },
]

const SupportTicketSearchForm = ({
    filterChanged,
    loading,
    statusOptions,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(SupportTicketSearchSchema),
        defaultValues: {
            SearchText: '',
            Status: 'all',
        },
    })

    const submit = async formData => {
        filterChanged(formData)
    }

    const resetForm = () => {
        reset({ SearchText: '', Status: 'all' })
        filterChanged({ SearchText: '', Status: 'all' })
    }

    return (
        <Form noValidate>
            <div className="my-2">
                <Form.Group widths="equal">
                    <FormTextFormField
                        control={control}
                        label="Search"
                        name="SearchText"
                        placeholder="Search"
                        errors={errors?.SearchText}
                        defaultValue=""
                    />
                    <FormDropdownField
                        control={control}
                        label="Status"
                        name="Status"
                        placeholder="All Statuses"
                        errors={errors?.Status}
                        options={statusOptions}
                        defaultValue="all"
                    />
                </Form.Group>
            </div>
            <SubmitCancelButtons
                loading={loading}
                onSubmit={handleSubmit(submit)}
                onCancel={resetForm}
            />
        </Form>
    )
}

export const SupportTicketListScreen = () => {
    const [filter, setFilter] = useState({ SearchText: '', Status: 'all' })
    const statusFilter = filter.Status === 'all' ? 'all' : filter.Status
    const { data, loading, refresh } = useSupportTicketsByEmail(statusFilter)
    const [selectedTicket, setSelectedTicket] = useState(null)
    const [detailModalOpen, setDetailModalOpen] = useState(false)
    const [filteredData, setFilteredData] = useState(data)

    // Update filteredData when data or filter changes
    React.useEffect(() => {
        let filtered = data

        // Apply search filter (client-side since API doesn't support search)
        if (filter.SearchText && filter.SearchText.trim()) {
            const searchValue = filter.SearchText.toLowerCase()
            filtered = filtered.filter(
                (ticket) =>
                    (ticket.TicketNumber && ticket.TicketNumber.toLowerCase().includes(searchValue)) ||
                    (ticket.Message && ticket.Message.toLowerCase().includes(searchValue)) ||
                    (ticket.IssueTypeName && ticket.IssueTypeName.toLowerCase().includes(searchValue)) ||
                    (ticket.RequesterName && ticket.RequesterName.toLowerCase().includes(searchValue)),
            )
        }

        setFilteredData(filtered)
    }, [data, filter])

    const handleViewTicket = (ticket) => {
        setSelectedTicket(ticket)
        setDetailModalOpen(true)
    }

    const handleCloseDetailModal = () => {
        setDetailModalOpen(false)
        setSelectedTicket(null)
        // Refresh tickets after viewing details
        refresh()
    }

    const handleStatusUpdate = () => {
        // Refresh tickets after status update
        refresh()
    }

    const handleCreateTicketSuccess = () => {
        refresh()
    }

    const handleDeleteTicket = async (ticketId) => {
        try {
            const { SupportTicketService } = await import('utility/services/support-ticket')
            await SupportTicketService.delete(ticketId)
            refresh()
        } catch (error) {
            console.error('Error deleting ticket:', error)
        }
    }

    const handleFilterChanged = (formData) => {
        setFilter({
            SearchText: formData.SearchText || '',
            Status: formData.Status || 'all',
        })
    }

    // Define the panes for the tabs
    const panes = [
        {
            menuItem: 'Raise Ticket',
            render: () => (
                <div>
                    <div className="col-12" style={{ maxWidth: '800px', margin: '0 auto' }}>
                        <RaiseTicketForm onSuccess={handleCreateTicketSuccess} />
                    </div>
                </div>
            ),
        },
        {
            menuItem: 'My Support Tickets',
            render: () => (
                <div>
                    {loading && <LoaderBar content="Loading tickets..." />}
                    <div className="col-12">
                        <SupportTicketSearchForm
                            statusOptions={STATUS_OPTIONS}
                            filterChanged={handleFilterChanged}
                            loading={loading}
                        />

                        {!loading && filteredData.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <p style={{ fontSize: '16px', color: '#666' }}>
                                    {filter.SearchText || filter.Status !== 'all'
                                        ? 'No tickets match your filters.'
                                        : 'You have not created any support tickets yet.'}
                                </p>
                            </div>
                        )}

                        {!loading && filteredData.length > 0 && (
                            <div className="my-3">
                                <SupportTicketList
                                    data={filteredData}
                                    onView={handleViewTicket}
                                    onDelete={handleDeleteTicket}
                                />
                            </div>
                        )}
                    </div>
                </div>
            ),
        },
    ]

    return (
        <div>
            <PageHeader header="Support Tickets" />
            {/* Tabs */}
            <Tab menu={{ secondary: true, pointing: true }} panes={panes} />

            {/* Detail Modal */}
            {selectedTicket && (
                <SupportTicketDetailModal
                    open={detailModalOpen}
                    onClose={handleCloseDetailModal}
                    ticketId={selectedTicket.TicketId}
                    onStatusUpdate={handleStatusUpdate}
                />
            )}
        </div>
    )
}
